/*
 * Unread replies next to the FAB, "peek then expand": a pill unrolls above
 * the launcher with a hairline timer draining along its top edge, furls back
 * into the launcher when the timer runs out, and expands into the unread list
 * on hover or activation.
 *
 * Same closed-shadow-root isolation as fab.css: selectors stay inside the
 * shadow tree and :host pins inherited text properties against host-page
 * styling.
 *
 * Furling dismisses the peek, not the notification. Once the timer runs out
 * the same reply never peeks again – it had its turn on screen – but nothing
 * is marked read: a missed support reply is costly, so the launcher badge
 * stays lit until the user opens the thread or marks it read.
 */

:host {
	--primary: hsl(260deg 100% 69%);
	--primary-dark: hsl(260deg 92% 57%);
	--ink: #14141f;
	--ink-muted: #3a3b47;
	--ink-subtle: #6b6c80;
	--hairline: rgb(20 20 31 / 10%);
	--peek: 8000ms;

	/* Direction the pill travels as it furls – toward the launcher. */
	--furl-x: 64px;

	position: fixed;
	right: 26px;
	bottom: 104px;
	z-index: 2147483647;

	font-family:
		system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial,
		sans-serif;
	font-size: 14px;
	line-height: 1.45;
	text-align: start;

	color-scheme: light;
}

:host([data-position="left"]) {
	--furl-x: -64px;
	right: auto;
	left: 26px;
}

/* Out of sight while the launcher is dragged, so it never floats apart from
   it. Visibility only – whether it shows at all is decided elsewhere. */
:host([data-launcher-moving]) {
	visibility: hidden;
}

/* --- the peek pill ------------------------------------------------------ */

.pill {
	position: relative;
	display: flex;
	gap: 9px;
	align-items: center;

	min-height: 46px;
	padding: 7px 8px 6px 10px;
	overflow: hidden;

	cursor: pointer;

	background: #fff;
	border: 1.5px solid var(--hairline);
	border-radius: 999px;
	box-shadow: 0 12px 32px rgb(20 20 31 / 18%);

	animation: rise 0.35s cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

.pill:hover {
	box-shadow: 0 16px 40px rgb(20 20 31 / 24%);
}

.pill:focus-visible {
	outline: 2px solid var(--primary);
	outline-offset: 3px;
}

/* Two stacked lines beside the disc: identity + excerpt, then rule title. */
.pill .main {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 1px;
	min-width: 0;
}

.pill .line {
	display: flex;
	gap: 6px;
	align-items: center;
	min-width: 0;
}

.pill .name {
	font-size: 13px;
	font-weight: 700;
	color: var(--ink);
	white-space: nowrap;
}

.pill .excerpt {
	flex: 1;
	min-width: 0;
	max-width: 180px;
	overflow: hidden;
	text-overflow: ellipsis;

	font-size: 13px;
	color: var(--ink-subtle);
	white-space: nowrap;
}

/* The matched rule's title, cropped so the pill always stays one line. */
.ruleTitle {
	overflow: hidden;
	text-overflow: ellipsis;

	font-size: 12px;
	font-weight: 600;
	color: var(--ink-subtle);
	white-space: nowrap;
}

/*
 * The drain bar sits flush inside the pill's top edge and empties toward the
 * launcher, so the pill reads as retreating before it moves.
 */
.timer {
	position: absolute;
	top: 0;
	right: 0;
	left: 0;

	height: 3px;

	background: linear-gradient(135deg, var(--primary), hsl(285deg 100% 62%));
	transform-origin: right;

	animation: drain var(--peek) linear both;
}

/* --- avatars ------------------------------------------------------------ */

.avatar {
	display: flex;
	flex: none;
	align-items: center;
	justify-content: center;

	width: 28px;
	height: 28px;

	font-size: 11px;
	font-weight: 700;
	color: var(--primary);

	background: hsl(260deg 100% 69% / 12%);
	border-radius: 10px;
}

.row .avatar {
	width: 30px;
	height: 30px;
}

.avatar[data-tone="status"] {
	color: hsl(160deg 72% 32%);
	background: hsl(160deg 70% 46% / 14%);
}

/*
 * Classification tints, mirroring the dashboard's `clsChipIcon` tones
 * (packages/ui tokens: info, rose, violet, warn) so the card and /rules
 * agree on what each classification looks like.
 */
.avatar svg {
	width: 15px;
	height: 15px;
}

.avatar[data-kind="it"],
.ruleTitle[data-kind="it"] {
	color: #1a6fbf;
}

.avatar[data-kind="it"] {
	background: rgb(61 157 255 / 14%);
}

.avatar[data-kind="bug"],
.ruleTitle[data-kind="bug"] {
	color: #b72c5a;
}

.avatar[data-kind="bug"] {
	background: rgb(224 74 124 / 13%);
}

.avatar[data-kind="feature"],
.avatar[data-kind="business"],
.ruleTitle[data-kind="feature"],
.ruleTitle[data-kind="business"] {
	color: #7b3fff;
}

.avatar[data-kind="feature"],
.avatar[data-kind="business"] {
	background: rgb(123 63 255 / 12%);
}

.avatar[data-kind="support"],
.ruleTitle[data-kind="support"] {
	color: #7a5500;
}

.avatar[data-kind="support"] {
	background: rgb(232 154 0 / 16%);
}

/* --- "+N more" ---------------------------------------------------------- */

.more {
	flex: none;

	height: 20px;
	padding: 0 8px;

	font-size: 11px;
	font-weight: 700;
	line-height: 20px;
	color: #fff;

	background: var(--primary);
	border-radius: 999px;
}

/* --- the expanded card -------------------------------------------------- */

.card {
	width: min(340px, calc(100vw - 52px));
	overflow: hidden;

	background: #fff;
	border: 1.5px solid var(--hairline);
	border-radius: 18px;
	box-shadow: 0 20px 44px rgb(20 20 31 / 22%);

	animation: rise 0.28s cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

.head {
	display: flex;
	gap: 8px;
	align-items: center;
	justify-content: space-between;

	padding: 12px 14px;

	border-bottom: 1px solid var(--hairline);
}

.eyebrow {
	overflow: hidden;
	text-overflow: ellipsis;

	font-size: 12px;
	font-weight: 700;
	color: var(--primary);
	white-space: nowrap;
}

.list {
	max-height: 264px;
	overflow-y: auto;
}

.row {
	display: flex;
	gap: 10px;
	align-items: center;

	width: 100%;
	padding: 12px 14px;

	font: inherit;
	text-align: start;

	cursor: pointer;

	background: hsl(260deg 100% 69% / 4%);
	border: 0;

	transition: background 0.15s ease;
}

.row:hover {
	background: hsl(260deg 100% 69% / 9%);
}

.row:focus-visible {
	outline: 2px solid var(--primary);
	outline-offset: -2px;
}

.body {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
}

.title {
	overflow: hidden;
	text-overflow: ellipsis;

	font-size: 13px;
	font-weight: 700;
	color: var(--ink);
	white-space: nowrap;
}

.row .excerpt {
	overflow: hidden;
	text-overflow: ellipsis;

	font-size: 13px;
	color: var(--ink-muted);
	white-space: nowrap;
}

.foot {
	display: flex;
	gap: 8px;

	padding: 12px 14px;

	border-top: 1px solid var(--hairline);
}

/* --- buttons ------------------------------------------------------------ */

.primary,
.outline {
	display: flex;
	align-items: center;
	justify-content: center;

	height: 36px;
	padding: 0 16px;

	font: inherit;
	font-size: 13px;
	font-weight: 600;

	cursor: pointer;

	border: 1px solid transparent;
	border-radius: 999px;

	transition:
		background 0.18s ease,
		color 0.18s ease;
}

.primary {
	flex: 1;

	color: #fff;

	background: var(--primary);
}

.primary:hover {
	background: var(--primary-dark);
}

.outline {
	padding: 0 14px;

	color: var(--ink-muted);

	background: #fff;
	border-color: var(--hairline);
}

.outline:hover {
	color: var(--ink);
	background: rgb(20 20 31 / 5%);
}

.ghost {
	padding: 0;

	font: inherit;
	font-size: 12px;
	color: var(--ink-subtle);

	cursor: pointer;

	background: none;
	border: 0;
}

.ghost:hover {
	color: var(--ink);
}

.ghost:focus-visible,
.primary:focus-visible,
.outline:focus-visible {
	outline: 2px solid var(--primary);
	outline-offset: 2px;
}

/* --- motion ------------------------------------------------------------- */

@keyframes rise {
	from {
		opacity: 0;
		transform: translateY(14px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes furl {
	0% {
		opacity: 1;
		transform: translateX(0) scale(1);
	}

	55% {
		opacity: 0.5;
	}

	100% {
		opacity: 0;
		transform: translateX(var(--furl-x)) scale(0.82);
	}
}

@keyframes drain {
	from {
		transform: scaleX(1);
	}

	to {
		transform: scaleX(0);
	}
}

/* Read by screen readers, never drawn. */
.live {
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	white-space: nowrap;
	clip-path: inset(50%);
}

/*
 * --- phase and anchor overrides -----------------------------------------
 *
 * Last in the file on purpose: every rule here outranks the component
 * defaults above, so ordering them together keeps specificity ascending.
 */

/* Only the parts hide – the host stays, so the live region is already on the
   page when the next headline is written to it. */
:host([data-phase="hidden"]) .pill,
:host(:not([data-phase="expanded"])) .card,
:host([data-phase="expanded"]) .pill {
	display: none;
}

:host([data-phase="furling"]) .pill {
	animation: furl 0.34s cubic-bezier(0.45, 0, 0.9, 0.6) both;
}

:host([data-phase="furling"]) .timer,
.pill:focus .timer {
	animation-play-state: paused;
}

/* Drain toward the launcher, whichever edge it is anchored to. */
:host([data-position="left"]) .timer {
	transform-origin: left;
}

/*
 * The timer keeps draining – it is information, not decoration – but the
 * pill and card arrive and leave without travelling.
 */
@media (prefers-reduced-motion: reduce) {
	.pill,
	.card,
	:host([data-phase="furling"]) .pill {
		animation-duration: 0.01ms;
	}
}
